import React from "react"
import { Typography, Grid, Box, useTheme } from "@mui/material"
import { Formik } from "formik"
import { useDispatch } from "react-redux"
import AppTextField from "components/StyledComponents/AppTextField"
import AppSelect from "components/StyledComponents/AppSelect"
import AppButton from "components/StyledComponents/AppButton"
import CommonModal from "components/CommonModal"
import { createSectionFieldAction, updateSectionFieldAction } from "redux/superAdmin/actions/user"
import { fieldTypeDescriptions, fieldTypes } from "helpers/constant"
import { validationSchemaField } from "helpers/validationSchema"

const FieldModal = ({
  open,
  onClose,
  isEdit,
  fieldData,
  sectionTypeId,
  sectionKey,
  existingFieldTypes = []
}) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const formikRef = React.useRef()
  const isTextSection = sectionKey === "TEXT"
  const isNotesSection = sectionKey === "NOTES"

  // For NOTES: only show field types not yet added; for TEXT: only rich_text
  const availableFieldTypes = (() => {
    if (isTextSection) return fieldTypes?.filter((t) => t?.value === "rich_text")
    if (isNotesSection) {
      const allowed = ["rich_text", "items"]
      return fieldTypes?.filter(
        (t) => allowed?.includes(t?.value) && !existingFieldTypes?.includes(t?.value)
      )
    }
    return fieldTypes
  })()

  const defaultFieldType =
    isTextSection || isNotesSection ? availableFieldTypes[0]?.value ?? "rich_text" : "number"

  const initialValues = {
    field_label: isEdit ? fieldData?.field_label : "",
    field_type: isEdit ? fieldData?.field_type : defaultFieldType,
    section_type: sectionTypeId
  }

  const handleSubmit = (values) => {
    const callback = () => {
      onClose()
    }

    if (isEdit) {
      dispatch(
        updateSectionFieldAction({
          id: fieldData?.id,
          payload: {
            field_label: values.field_label,
            field_type: values.field_type,
            section_type: sectionTypeId,
            field_name: values.field_label.toLowerCase().replace(/\s/g, "_")
          },
          callback
        })
      )
    } else {
      dispatch(
        createSectionFieldAction({
          payload: {
            field_label: values.field_label,
            field_type: values.field_type,
            section_type: sectionTypeId,
            field_name: values.field_label.toLowerCase().replace(/\s/g, "_")
          },
          callback
        })
      )
    }
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchemaField}
      onSubmit={handleSubmit}
      enableReinitialize={true}>
      {(formik) => (
        <CommonModal open={open} onClose={onClose} maxWidth="sm" hideAction={true}>
          <Typography variant="h2" className="mb-4 text-center" color="white">
            {isEdit ? "Edit Field" : "Add New Field"}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <AppTextField
                name="field_label"
                label="Field Label (Visible to user)"
                placeholder="e.g. Manager Notes"
                value={formik.values.field_label}
                onChange={formik.handleChange}
                isRequired={true}
                isUpperCase={true}
                onBlur={formik.handleBlur}
                error={formik.touched.field_label && Boolean(formik.errors.field_label)}
                helperText={formik.touched.field_label && formik.errors.field_label}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <AppSelect
                name="field_type"
                label="Field Type"
                value={formik.values.field_type}
                isRequired={true}
                onChange={formik.handleChange}
                menuItems={
                  isEdit && isNotesSection
                    ? fieldTypes.filter((t) => t.value === fieldData?.field_type)
                    : availableFieldTypes
                }
                disabled={isEdit}
              />
              {fieldTypeDescriptions[formik.values.field_type] && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: theme.palette.primary.hover
                  }}>
                  <Typography variant="body2" color="rgba(255,255,255,0.6)">
                    {fieldTypeDescriptions[formik.values.field_type]}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} className="d-flex justify-content-center gap-3 mt-2">
              <AppButton onClick={onClose} variant="outlined" sx={{ height: 48, width: 150 }}>
                Cancel
              </AppButton>
              <AppButton onClick={formik.handleSubmit} sx={{ height: 48, width: 150 }}>
                {isEdit ? "Update" : "Save"}
              </AppButton>
            </Grid>
          </Grid>
        </CommonModal>
      )}
    </Formik>
  )
}

export default FieldModal
